import { computed, toValue, type ComputedRef, type MaybeRefOrGetter } from 'vue'
import type { ProjectCalculationTierBoundary } from '@arcexperts/core-types'
import {
  useProjectArchitectCalculator,
  useProjectLandscapeArchitectCalculator,
  useProjectCivilEngineerCalculator,
  useProjectCivilEngineerExcavationCalculator,
  useProjectEngineerCalculator,
  useProjectElectricalCalculator,
  useProjectHeatingCoolingCalculator,
  useProjectVentilationACCalculator,
  useProjectHVACCalculator,
  useProjectSanitaryCalculator,
  useProjectBuildingAutomationCalculator,
  useProjectSpecialistCoordinationCalculator,
} from '@/stores/projectCenter/projectServiceCalculatorFactory'

export type Role =
  | 'ARCHITECT'
  | 'LANDSCAPE_ARCHITECT'
  | 'CIVIL_ENGINEER'
  | 'CIVIL_ENGINEER_EXCAVATION'
  | 'ENGINEER'
  | 'ELECTRICAL'
  | 'HEATING_COOLING'
  | 'VENTILATION_AC'
  | 'HVAC'
  | 'SANITARY'
  | 'BUILDING_AUTOMATION'
  | 'SPECIALIST_COORDINATION'

export interface ProjectCalculator {
  tierBoundariesForUsages?: (ProjectCalculationTierBoundary | null)[]
  getDefaultUsageItem: unknown
  getMinutesPerSquareMeterForUsageAtIndexIgnoringManualArea: (index: number) => number
  setComplexityForUsages: () => void
}

type CalculatorFactory = () => ProjectCalculator

const calculatorMap: Record<Role, CalculatorFactory> = {
  ARCHITECT: useProjectArchitectCalculator,
  LANDSCAPE_ARCHITECT: useProjectLandscapeArchitectCalculator,
  CIVIL_ENGINEER: useProjectCivilEngineerCalculator,
  CIVIL_ENGINEER_EXCAVATION: useProjectCivilEngineerExcavationCalculator,
  ENGINEER: useProjectEngineerCalculator,
  ELECTRICAL: useProjectElectricalCalculator,
  HEATING_COOLING: useProjectHeatingCoolingCalculator,
  VENTILATION_AC: useProjectVentilationACCalculator,
  HVAC: useProjectHVACCalculator,
  SANITARY: useProjectSanitaryCalculator,
  BUILDING_AUTOMATION: useProjectBuildingAutomationCalculator,
  SPECIALIST_COORDINATION: useProjectSpecialistCoordinationCalculator,
}

export function useCalculatorForRole(role: MaybeRefOrGetter<Role | undefined>): ComputedRef<ProjectCalculator> {
  return computed(() => {
    const currentRole = toValue(role)
    const calculatorFn = currentRole ? calculatorMap[currentRole] : calculatorMap.ARCHITECT
    return calculatorFn()
  })
}
